import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import JobReviewStatusTabs from './components/JobReviewStatusTabs';
import JobReviewFilters from './components/JobReviewFilters';
import JobReviewTable from './components/JobReviewTable';
import StatusUpdatePopover from './components/StatusUpdatePopover';
import EmptyStateJobReview from './components/EmptyStateJobReview';
import { usePaginatedPayRunMemberJobsQuery } from './hooks/usePaginatedPayRunMemberJobsQuery';
import { selectSelectedReviewTab, selectStatusCounts, setSelectedReviewTab } from './confirmJobsSlice';
import { useUpdateBulkJobStatusMutation } from './api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import GenericModal from 'components/generic-modal/GenericModal';
import { CrossIconvector, EditIconBlue } from 'assets/icons';
import { getJobReviewStatusMap } from '../utils';
import { AmountFilterOperation } from 'constants/common';
import DateUtils from 'utils/dateUtils';
import { CustomizeColumnsSource, JobReviewStatus, JobReviewTabStatus } from '../constants';
import { Loader } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../events';
import { PayRunMemberJob } from './confirmJob.model';
import CustomizeColumnsModal, { ColumnConfig } from 'components/customize-columns-modal/CustomizeColumnsModal';
import { useSaveCustomColumnsMutation } from '../api';

interface ConfirmJobsTabProps {
  setJobNavigationFunctions?: (functions: {
    getNextJob: (currentJobId: string) => { jobId: string; status: string };
    getPreviousJob: (currentJobId: string) => { jobId: string; status: string };
  }) => void;
}

const ConfirmJobsTab: FC<ConfirmJobsTabProps> = ({ setJobNavigationFunctions }) => {
  const { payRunId, merchantId } = useParams();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const statusCounts = useAppSelector(selectStatusCounts);
  const selectedReviewTab = useAppSelector(selectSelectedReviewTab);
  const [selectedJobs, setSelectedJobs] = useState<PayRunMemberJob[]>([]);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState<string>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [statusToConfirm, setStatusToConfirm] = useState<string | null>(null);

  // Filter states in parent component
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [amountFilter, setAmountFilter] = useState({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });
  const [scheduleStartDate, setScheduleStartDate] = useState<string | null>(null);
  const [scheduleEndDate, setScheduleEndDate] = useState<string | null>(null);
  const [externalCompanies, setExternalCompanies] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);
  const [showCustomizeColumns, setShowCustomizeColumns] = useState(false);

  const {
    isFetching: isFetchingJobs,
    list: jobs,
    totalCount,
    fetchData,
    setQueryParams,
    page,
    queryParams,
    columnConfig
  } = usePaginatedPayRunMemberJobsQuery(payRunId, merchantId);

  const [updateBulkJobStatus, { isLoading: isActionInProgress }] = useUpdateBulkJobStatusMutation();
  const [saveCustomColumns, { isLoading: isSavingColumns }] = useSaveCustomColumnsMutation();

  useEffect(() => {
    const jobReviewStatus =
      selectedReviewTab === JobReviewTabStatus.ALL ? null : (selectedReviewTab as unknown as JobReviewStatus);
    setQueryParams(prev => ({
      ...prev,
      jobReviewStatus
    }));
  }, [selectedReviewTab]);

  const onTabChange = status => {
    dispatch(setSelectedReviewTab(status));
    setSelectedJobs([]);

    // Reset filter states directly
    setSelectedCustomers([]);
    setSelectedMerchants([]);
    setLeadSources([]);
    setAmountFilter({
      filterOperation: AmountFilterOperation.ALL,
      minAmount: null,
      maxAmount: null
    });
    setScheduleStartDate(null);
    setScheduleEndDate(null);
    setExternalCompanies([]);
    setServiceAreas([]);
  };

  const onPageChange = (pageNumber: number) => {
    fetchData(pageNumber);
  };

  const handleJobCheckboxSelect = (job: PayRunMemberJob) => {
    setSelectedJobs(prev => {
      const isJobSelected = prev.some(j => j.jobId === job.jobId);
      if (isJobSelected) {
        return prev.filter(j => j.jobId !== job.jobId);
      }
      return [...prev, job];
    });
  };

  const handleSelectAllJobs = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs);
    }
  };

  const handleBulkStatusChange = (status: string) => {
    setBulkUpdateStatus(status);
  };

  const handleShowConfirmModal = (status: string) => {
    setStatusToConfirm(status);
    setShowConfirmModal(true);
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedJobs.length === 0 || !statusToConfirm) return;
    try {
      await updateBulkJobStatus({
        teamId: currentTeam?.id.toString(),
        payRunId,
        merchantId,
        status: statusToConfirm,
        jobIds: selectedJobs.map(job => job.jobId)
      });
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunJobStatusChange, {
        payRunId,
        merchantId,
        status: statusToConfirm,
        jobCount: selectedJobs.length
      });

      // Clear selections after successful update
      setSelectedJobs([]);
    } catch (error) {
      //error handling
    } finally {
      setShowConfirmModal(false);
    }
  };

  const getModalTitle = () => {
    if (!statusToConfirm) return '';
    return `${getJobReviewStatusMap(statusToConfirm).label} jobs`;
  };

  // Expose job navigation functions
  useEffect(() => {
    if (setJobNavigationFunctions && jobs.length > 0) {
      setJobNavigationFunctions({
        getNextJob: currentJobId => {
          const currentIndex = jobs.findIndex(job => job.jobId === currentJobId);
          if (currentIndex !== -1 && currentIndex < jobs.length - 1) {
            return {
              jobId: jobs[currentIndex + 1].jobId,
              status: jobs[currentIndex + 1].status
            };
          }
          return { jobId: '', status: '' };
        },
        getPreviousJob: currentJobId => {
          const currentIndex = jobs.findIndex(job => job.jobId === currentJobId);
          if (currentIndex > 0) {
            return {
              jobId: jobs[currentIndex - 1].jobId,
              status: jobs[currentIndex - 1].status
            };
          }
          return { jobId: '', status: '' };
        }
      });
    }
  }, [jobs, setJobNavigationFunctions]);

  // Individual filter handlers
  const handleCustomersChange = (customers: string[]) => {
    setSelectedCustomers(customers);
    setQueryParams(prev => ({ ...prev, customerIds: customers.length > 0 ? customers.join(',') : null }));
  };

  const handleMerchantsChange = (merchants: string[]) => {
    setSelectedMerchants(merchants);
    setQueryParams(prev => ({ ...prev, createdBy: merchants.length > 0 ? merchants.join(',') : null }));
  };

  const handleLeadSourcesChange = (sources: string[]) => {
    setLeadSources(sources);
    setQueryParams(prev => ({ ...prev, leadSource: sources.length > 0 ? sources.join(',') : null }));
  };

  const handleAmountFilterChange = (filter: {
    filterOperation: AmountFilterOperation;
    minAmount: number | null;
    maxAmount: number | null;
  }) => {
    const { filterOperation, minAmount, maxAmount } = filter;
    let amount = `${filterOperation}:${minAmount}`;
    if (filterOperation === AmountFilterOperation.ALL) amount = '';
    if (filterOperation === AmountFilterOperation.BW) amount = `${filterOperation}:${minAmount}:${maxAmount}`;
    setAmountFilter(filter);
    setQueryParams(prev => ({
      ...prev,
      amount
    }));
  };

  const handleScheduleStartDateChange = (date: string | null) => {
    setScheduleStartDate(date);
    setQueryParams(prev => ({ ...prev, scheduledFrom: DateUtils.startDateToUTCString(date) }));
  };

  const handleScheduleEndDateChange = (date: string | null) => {
    setScheduleEndDate(date);
    setQueryParams(prev => ({ ...prev, scheduledTo: DateUtils.endDateToUTCString(date) }));
  };

  const handleExternalCompaniesChange = (companies: string[]) => {
    setExternalCompanies(companies);
    setQueryParams(prev => ({ ...prev, externalCompany: companies.length > 0 ? companies.join(',') : null }));
  };

  const handleTagsChange = (tagsArray: string[]) => {
    setTags(tagsArray);
    setQueryParams(prev => ({ ...prev, tags: tagsArray }));
  };

  const handleServiceAreasChange = (areas: string[]) => {
    setServiceAreas(areas);
    setQueryParams(prev => ({ ...prev, serviceAreas: areas.length > 0 ? areas.join(',') : null }));
  };

  const handleSortChange = (newSortBy: string, newSortOrder: string) => {
    setQueryParams(prev => ({
      ...prev,
      sortBy: newSortBy,
      sortOrder: newSortOrder
    }));
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunSortByUse, {
      payRunId,
      table: 'pay-run-confirm-jobs',
      sortBy: newSortBy,
      sortOrder: newSortOrder
    });
  };
  const handleSaveColumns = async (updatedColumns: ColumnConfig[]) => {
    if (!currentTeam?.id) return;
    try {
      await saveCustomColumns({
        teamId: currentTeam.id,
        payload: {
          feature: CustomizeColumnsSource.PAYRUN_MEMBER_REVIEW,
          columnInfo: updatedColumns
        }
      });
      logAnalyticEvent(PayRunsCleverTapEvents.webCustomizeColumnsSaved, {
        source: CustomizeColumnsSource.PAYRUN_MEMBER_REVIEW
      });
    } finally {
      setShowCustomizeColumns(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <JobReviewStatusTabs onChange={onTabChange} statusCounts={statusCounts} />
      <div className="flex items-center justify-between p-4">
        <JobReviewFilters
          selectedCustomers={selectedCustomers}
          setSelectedCustomers={handleCustomersChange}
          selectedMerchants={selectedMerchants}
          setSelectedMerchants={handleMerchantsChange}
          leadSources={leadSources}
          setLeadSources={handleLeadSourcesChange}
          amountFilter={amountFilter}
          setAmountFilter={handleAmountFilterChange}
          scheduleStartDate={scheduleStartDate}
          setScheduleStartDate={handleScheduleStartDateChange}
          scheduleEndDate={scheduleEndDate}
          setScheduleEndDate={handleScheduleEndDateChange}
          externalCompanies={externalCompanies}
          setExternalCompanies={handleExternalCompaniesChange}
          selectedTags={tags}
          setSelectedTags={handleTagsChange}
          selectedServiceAreas={serviceAreas}
          setSelectedServiceAreas={handleServiceAreasChange}
        />
        <div className="flex items-center gap-4">
          <div
            onClick={() => setShowCustomizeColumns(true)}
            className="flex cursor-pointer items-center justify-between gap-2 whitespace-nowrap 
          p-2 text-17px font-semibold text-secondary">
            Edit table
            <div className="p-1">
              <EditIconBlue className="h-5 w-5" />
            </div>
          </div>
          <StatusUpdatePopover
            onStatusChange={handleBulkStatusChange}
            currentStatus={bulkUpdateStatus}
            onShowConfirmModal={handleShowConfirmModal}
            disabled={!selectedJobs.length}
            hasDraftAdjustment={selectedJobs?.some(job => job.hasDraftAdjustment)}
          />
        </div>
      </div>
      {isFetchingJobs && <Loader />}
      {!isFetchingJobs && jobs.length === 0 && <EmptyStateJobReview />}
      {!isFetchingJobs && jobs.length > 0 && (
        <JobReviewTable
          jobs={jobs}
          page={page}
          totalCount={totalCount}
          onPageChange={onPageChange}
          selectedJobs={selectedJobs}
          onJobCheckboxSelect={handleJobCheckboxSelect}
          onSelectAll={handleSelectAllJobs}
          fetchData={fetchData}
          onSortChange={handleSortChange}
          currentSortBy={queryParams.sortBy}
          currentSortOrder={queryParams.sortOrder}
          columnInfo={columnConfig}
        />
      )}

      {/* Confirmation Modal */}
      <GenericModal
        showModal={showConfirmModal}
        isActionInProgress={isActionInProgress}
        CloseIcon={CrossIconvector}
        title={getModalTitle()}
        subTitle={`Are you sure you want to move ${selectedJobs.length} jobs to ${
          getJobReviewStatusMap(statusToConfirm).label
        }?`}
        primaryBtnLabel="Yes, move"
        onPrimaryBtnClick={handleBulkStatusUpdate}
        secondaryBtnLabel="No, Cancel"
        onSecondaryBtnClick={() => setShowConfirmModal(false)}
        primaryBtnId="confirm-status-change"
        secondaryBtnId="cancel-status-change"
        containerWidth="343px"
        className="px-4 py-[30px]"
        titleStyle="!text-xl font-semibold text-headingGray mb-2"
        subTitleStyle="text-sbase font-semibold text-headingGray text-center mb-4"
        onCloseIconClick={() => setShowConfirmModal(false)}
      />
      {showCustomizeColumns && (
        <CustomizeColumnsModal
          open={showCustomizeColumns}
          onClose={() => setShowCustomizeColumns(false)}
          columns={columnConfig}
          onSave={handleSaveColumns}
          reorderableStartIndex={1}
          isLoading={isSavingColumns}
        />
      )}
    </div>
  );
};

export default ConfirmJobsTab;
